import EastIcon from "@mui/icons-material/East";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WestIcon from "@mui/icons-material/West";
import { Avatar, IconButton, List, ListItem } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { apiEndpoints, httpMethods } from "../../../constants/api.constants";
import useAPI from "../../../hooks/useAPI";
import useI18N from "../../../hooks/useI18N";
import {
  Inspection,
  MetalStonePools,
  MusharkaContract,
  Request,
  Transaction,
} from "../../../shared/icon";
import { MUIStyled } from "../../../shared/MUIStyled";
import SABox from "../../../shared/SABox";
import SALoader from "../../../shared/SALoader";
import SAStack from "../../../shared/SAStack";
import SATruncateWithEllipsis from "../../../shared/SATruncateWithEllipsis";
import SATypography from "../../../shared/SATypography";
import {
  decodeToken,
  formatTimestamp,
  getNotifications,
} from "../../../utils/functions";
import { notificationModuleMap } from "../../Notifications";

const NotificationPanel = MUIStyled(SABox)({
  padding: 16,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
});

const NotificationList = MUIStyled(List)(({ theme }) => ({
  padding: 0,
  "& .MuiListItem-root": {
    marginTop: 18,
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    cursor: "pointer",
    position: "relative",
    padding: 20,
    transition: "all 0.3s ease",
    "&:hover": { background: "rgba(233, 233, 233, 0.05)" },
  },
}));

const CloseArrowButton = MUIStyled(IconButton)(({ dir }) => ({
  position: "absolute",
  top: 12,
  [dir === "rtl" ? "left" : "right"]: 12,
}));

const moduleIconMap = {
  transactions: <Transaction />,
  "purchase-requests": <Request />,
  "musharka-contracts": <MusharkaContract />,
  "metals-stones-pools": <MetalStonePools />,
  "request-management": <Request />,
  "inspection-requests": <Inspection />,
  "manufacturer/processing-details": <Inspection />,
  "request-management/musharakah": <MusharkaContract />,
  "request-management/pool": <MetalStonePools />,
};

const MODULE_META_MAP = {
  "request-management/musharakah": {
    homeRoute: "/requests-management",
    label: "request-management",
    href: "/request-management?tab=taqabeth-requests",
  },
  "request-management/pool": {
    homeRoute: "/requests-management",
    label: "request-management",
    href: "/request-management?tab=taqabeth-requests",
  },
};

const STATE_MODULES = [
  "request-management/musharakah",
  "request-management/pool",
];

const NotificationDrawer = ({ onClose }) => {
  const { t, dir } = useI18N();
  const { api } = useAPI();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const hasScrolledRef = useRef(null);

  const notificationsData = useSelector((state) => state.app?.notifications);

  const [page, setPage] = useState(1);
  const [next, setNext] = useState(notificationsData?.next);
  const [performanceLoader, setPerformanceLoader] = useState(false);
  const [notifications, setNotifications] = useState(
    notificationsData?.results || [],
  );

  const { role } = decodeToken(localStorage.getItem("accessToken"));

  // Define allowed notification types per role (object)
  const ENFORCER_TYPES = {
    PURCHASE_REQUEST_APPROVED: "request-management",
    SALE_REQUEST_APPROVED: "request-management",
    MUSHARAKAH_CONTRACT_REQUEST_APPROVED: "request-management/musharakah",
    ASSET_CONTRIBUTED_IN_POOL: "request-management/pool",
    POOL_CONTRIBUTION_APPROVED: "request-management/pool",
  };

  const INSPECTOR_TYPES = {
    JEWELRY_PRODUCT_INSPECTION: "inspection-requests",
    JEWELRY_PRODUCTION_STATUS_UPDATED: "inspection-requests",
    JEWELRY_PRODUCTION_DELIVERY_STATUS: "inspection-requests",
  };

  // Filter notifications based on role
  let filteredNotifications = notifications;
  if (role === "TAQABETH_ENFORCER") {
    filteredNotifications = notifications.filter((n) =>
      Object.keys(ENFORCER_TYPES).includes(n.notification_type),
    );
  } else if (role === "JEWELLERY_INSPECTOR") {
    filteredNotifications = notifications.filter((n) =>
      Object.keys(INSPECTOR_TYPES).includes(n.notification_type),
    );
  }

  const buildNavigateState = (notificationType) => {
    const moduleKey = notificationModuleMap[notificationType];
    if (!STATE_MODULES.includes(moduleKey)) return null;
    const meta = MODULE_META_MAP[moduleKey] || {};
    return {
      homeRoute: meta.homeRoute || moduleKey || "",
      label: meta.label || moduleKey || "",
      href: meta.href || (moduleKey ? `${moduleKey}` : ""),
    };
  };

  const handleReadNotification = async (id) => {
    const response = await api({
      method: httpMethods.patch,
      endPoint: `${apiEndpoints.readUnreadNotification}${id}/`,
      showToastOnError: true,
    });
    if (response?.status) getNotifications();
  };

  const handleNotificationClick = ({ id, type }) => {
    const currentRoute = notificationModuleMap[type];
    if (!currentRoute) return;
    onClose();

    const navState = buildNavigateState(type);
    if (navState) {
      navigate(`/${currentRoute}/${id}`, { state: navState });
    } else {
      navigate(`/${currentRoute}/${id}`);
    }
  };

  const handleScroll = useCallback(async () => {
    const container = containerRef.current;
    if (!container || performanceLoader) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isNearBottom && !hasScrolledRef.current) {
      hasScrolledRef.current = true;

      if (!next) return;

      const nextPage = page + 1;
      setPage(nextPage);

      setPerformanceLoader(true);
      const response = await getNotifications({
        page: nextPage,
        needLoader: true,
        storeInRedux: false,
      });

      if (response?.status === 200) {
        const { results, next } = response?.data?.data || {};
        setNext(next);
        setNotifications((prev) => [...prev, ...(results || [])]);
      }

      setPerformanceLoader(false);
    } else if (!isNearBottom) {
      hasScrolledRef.current = false;
    }
  }, [page, next, performanceLoader]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (performanceLoader) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth",
          }),
        ),
      );
    }
  }, [performanceLoader]);

  return (
    <NotificationPanel ref={containerRef} onScroll={handleScroll}>
      <SAStack direction="row" gap={2} alignItems="center">
        <NotificationsActiveIcon sx={{ color: "gray.light300" }} />
        <SATypography variant="h5" fontWeight="bold" color="gray.light300">
          {t("notifications")}
        </SATypography>
        <CloseArrowButton dir={dir} onClick={onClose}>
          {dir === "rtl" ? (
            <WestIcon fontSize="medium" sx={{ zIndex: 1000 }} />
          ) : (
            <EastIcon fontSize="medium" sx={{ zIndex: 1000 }} />
          )}
        </CloseArrowButton>
      </SAStack>
      <NotificationList dir={dir}>
        {filteredNotifications?.map((notification) => {
          const {
            id,
            title,
            message,
            is_read,
            object_id,
            created_at,
            notification_type,
          } = notification || {};
          return (
            <ListItem
              key={id}
              onClick={() => {
                handleNotificationClick({
                  id: object_id,
                  type: notification_type,
                });
                handleReadNotification(id);
              }}
            >
              <Avatar
                variant="circular"
                sx={{ bgcolor: "#dfe9f8", width: 40, height: 40 }}
              >
                {moduleIconMap[notificationModuleMap[notification_type]] || (
                  <InfoOutlineIcon sx={{ color: "primary.main" }} />
                )}
              </Avatar>
              <SABox
                flex={1}
                sx={{ ...(dir === "rtl" ? { textAlign: "right" } : {}) }}
              >
                <SATypography
                  fontWeight="bold"
                  variant="subtitle1"
                  color="text.primary"
                  sx={{
                    display: "flex",
                    // alignItems: "center",
                    gap: "5px",
                  }}
                >
                  {!is_read && (
                    <SABox
                      sx={{
                        position: "absolute",
                        top: "13px",
                        left: "10px",
                        width: "7px",
                        height: "7px",
                        background: "green",
                        borderRadius: "50%",
                      }}
                    ></SABox>
                  )}
                  {title}
                </SATypography>
                <SATypography
                  mt={0.5}
                  variant="body2"
                  color="gray.light300"
                  lineHeight={1.5}
                  fontWeight={500}
                >
                  <SATruncateWithEllipsis text={message} maxLength={80} />
                </SATypography>
                <SATypography
                  mt={1}
                  variant="caption"
                  color="black.light"
                  fontStyle="italic"
                >
                  {formatTimestamp(created_at)}
                </SATypography>
              </SABox>
            </ListItem>
          );
        })}
      </NotificationList>
      {performanceLoader && (
        <SABox display="flex" justifyContent="center" py={2} dir={dir}>
          <SALoader />
        </SABox>
      )}
    </NotificationPanel>
  );
};

export default NotificationDrawer;
